import React, { ReactNode, useEffect, useState } from 'react';
import { PersonStatusDTO } from '../../../../api/core/models';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { IntlShape, useIntl } from 'react-intl';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { getDefaultPersonStatus } from '../../../../Settings/emptyModels';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { ValidationResult, ValidationResults } from '../../../../util/validation/interfaces';
import { SelectItem } from 'primereact/selectitem';
import { InputGroup } from '../layout/InputGroup';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { Panel } from '../layout/Panel';

interface StatusesProps {
  title: string;
  checkPermission: CheckPermissionFunction;
  statuses: PersonStatusDTO[];
  onChange: (statuses: PersonStatusDTO[]) => void;
  validation: ValidationResult;
  autoFocus: boolean;
}

export function PersonStatuses(props: StatusesProps): ReactNode {
  const intl: IntlShape = useIntl();
  const affiliations: SelectItem[] = getVocabularyOptions(intl.locale, 'affiliations_list', '');

  return (
    <Panel title={props.title} toggleable>
      <MultiInputControls<PersonStatusDTO>
        canEdit={props.checkPermission().canWrite}
        min={1}
        addNew={r => r.push(getDefaultPersonStatus())}
        lines={props.statuses}
        onChange={props.onChange}
        renderLines={(status: PersonStatusDTO, onLineChange: (personStatusDTO: PersonStatusDTO) => void, actions: Actions, idx: number): ReactNode => {
          return (
            <>
              {affiliations && <StatusAffiliationConnect
                idx={idx}
                autoFocus={props.autoFocus}
                label={props.title}
                checkPermission={props.checkPermission}
                statusObject={status}
                onChange={onLineChange}
                validation={props.validation.children[idx]}
                affiliations={affiliations}
                deleteAction={actions.delete}
              />}
            </>
          );
        }}
      />
    </Panel>
  );
}

/*
 * Connects Status and Affiliation
 * The Status select-list depends on the Affiliation selected
 * E.g. Affiliation RUB has another affiliations_list (Vocabulary) then TUDO
 */
interface StatusAffiliationConnectProps {
  idx: number;
  label: string;
  checkPermission: CheckPermissionFunction;
  affiliations: SelectItem[];
  statusObject: PersonStatusDTO;
  onChange: (line: any) => void;
  validation: ValidationResults;
  deleteAction: (e: React.MouseEvent<any>) => void;
  autoFocus: boolean;
}

function StatusAffiliationConnect(props: StatusAffiliationConnectProps) {
  const intl: IntlShape = useIntl();
  const [statusOptions, setStatusOptions] = useState<SelectItem[]>(props.statusObject.affiliation ?
    getVocabularyOptions(intl.locale, 'person_status_list', props.statusObject.affiliation) : []);

  useEffect((): void => {
    if(!props.statusObject.status) return;
    if (!statusOptions.map((so: SelectItem): any => so.value).includes(props.statusObject.status)) {
      props.onChange({...props.statusObject, status: null});
    }
  }, [statusOptions]);

  const updateStatusOptions = (affiliation: string): void => {
    if (!affiliation) {
      return setStatusOptions([]);
    }
    const options: SelectItem[] = getVocabularyOptions(intl.locale, 'person_status_list', affiliation);
    setStatusOptions(options);
  };

  return (
    <InputGroup
      delete={{
        action: props.deleteAction,
        disabled: !props.checkPermission().canWrite,
      }}
    >
      <LabelledDropdown
        id={`personStatus-${props.idx}-affiliation`}
        autoFocus={props.autoFocus}
        value={props.statusObject.affiliation}
        permissions={props.checkPermission('affiliation')}
        validation={props.validation?.fields?.affiliation}
        options={props.affiliations}
        onChange={(affiliation: any): void => {
          updateStatusOptions(affiliation);
          props.onChange({...props.statusObject, affiliation});
        }}

        className={'w-15rem'}
        label={`${props.label}.attr.affiliation`}
      />
      <LabelledDropdown
        id={`personStatus-${props.idx}-status`}
        value={props.statusObject.status}
        permissions={props.checkPermission('status')}
        validation={props.validation?.fields?.status}
        disabled={statusOptions.length === 0}
        options={statusOptions}
        onChange={(status: any): void => props.onChange({...props.statusObject, status})}
        label={`${props.label}.attr.status`}
        className={'w-15rem'}
      />
    </InputGroup>
  );
}
